// AttachmentMessageBubble — render de anexo (imagem/arquivo) de mensagem de
// atendimento, COMPARTILHADO entre o SessionChat (atendente) e o portal (parceiro).
//
// Duas fontes de conteúdo, ambas suportadas:
//  - storage (anexo do ATENDENTE): `content.url` re-assinada FRESCA no render pelo
//    backend (`resign_attachment_content`) a partir do `storage_key` — nunca
//    persistida (senão >7d = 403).
//  - legado b64 (anexo antigo do PARCEIRO): `content.attachment_b64` inline → data-uri.
//
// Imagem → thumbnail + lightbox (reusa `ImagePreview` do chat). Arquivo previewável
// (pdf/csv/md/office) COM storage_key → tile + "Visualizar" (`FilePreviewDialog`).
// Resto → tile de download.
//
// Duas políticas por prop:
//  - `downloadable` (default true): false esconde as afford de download (view-only,
//    política do áudio). Hoje ambos os lados passam true (decisão do Augusto).
//    Vale também para o "Baixar imagem" da barra do visualizador (16/10/2026).
//  - `allowPreview` (default true): o portal passa FALSE — o "Visualizar" bate no
//    dispatcher interno `/file-preview/` que o parceiro (portalApi) não alcança; a
//    signed-url do `<img>`/download é HMAC (sem auth), então imagem+download rodam
//    pros dois. Sem essa distinção o parceiro clicaria "Visualizar" e tomaria 401/403.

import { useState } from "react";
import { Eye, Paperclip } from "lucide-react";

import { Button } from "@/components/ui/button";
import { FilePreviewDialog } from "@/components/file-preview/FilePreviewDialog";
import { ImagePreview } from "@/components/file-preview/ImagePreview";
import { VideoAttachment } from "@/components/common/VideoAttachment";
import { TextoComLinks } from "@/components/links/TextoComLinks";
import { classifyPreview, storageKeyFromSignedUrl } from "@/services/filePreviewService";
import { cn } from "@/lib/utils";
import type { AttachmentMessageContent } from "@/types/atendimento";

// Re-export pro contrato ficar junto do componente (SessionChat/portal importam daqui).
export type { AttachmentMessageContent };

export interface AttachmentMessageBubbleProps {
  content: AttachmentMessageContent;
  /** default true; false = view-only (esconde download, política do áudio). */
  downloadable?: boolean;
  /** default true; false no portal (parceiro não alcança o dispatcher /file-preview). */
  allowPreview?: boolean;
  /**
   * Tom da BOLHA que hospeda este anexo (redesign v2): `"own"` = bolha própria
   * (`.bubble-own`/emerald) → chip claro translúcido (`bg-white/10`); `"other"` =
   * bolha alheia (`bg-background` frost) → chip suave. Mantém legibilidade em
   * light E dark (o chip usa branco, tema-independente). Default `"other"`.
   */
  tone?: "own" | "other";
  className?: string;
}

function bytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

// Mimes de imagem RASTER seguros pra montar data-URI do b64 LEGADO do parceiro.
// NUNCA svg/html/xml: renderizar conteúdo ATIVO controlado pelo parceiro num
// `data:` de mesma origem é footgun de XSS/phishing. O storage (url HMAC) é imune.
const SAFE_B64_IMAGE_MIMES = new Set(["image/png", "image/jpeg", "image/gif", "image/webp"]);

export function AttachmentMessageBubble({
  content,
  downloadable = true,
  allowPreview = true,
  tone = "other",
  className,
}: AttachmentMessageBubbleProps) {
  const [previewOpen, setPreviewOpen] = useState(false);

  // Chip do anexo ciente do tom da bolha (redesign): own (bolha escura) → chip
  // claro translúcido; other (bolha clara) → chip suave. Sem branco/azul cru.
  const onOwn = tone === "own";
  const tileBase = onOwn
    ? "border-white/25 bg-white/10 text-white"
    : "border-border bg-background/60 text-foreground";
  const tileHover = onOwn ? "hover:bg-white/20" : "hover:bg-background/90";
  const subText = onOwn ? "text-white/80" : "text-muted-foreground";

  // Fonte: url de storage (re-assinada, HMAC) tem prioridade. Pro b64 LEGADO do
  // parceiro só montamos data-URI se o mime for de imagem raster SEGURA (allow-list);
  // svg/html/pdf/etc. do parceiro NÃO viram data-URI (cai no rótulo abaixo).
  const b64Mime = (content.mime || "").toLowerCase();
  const b64Src =
    content.attachment_b64 && SAFE_B64_IMAGE_MIMES.has(b64Mime)
      ? `data:${b64Mime};base64,${content.attachment_b64}`
      : null;
  const src = content.url || b64Src;

  const filename = content.filename || "anexo";
  const size = content.size || 0;

  if (!src) {
    // Sem url de storage (fail-closed) e sem b64 de imagem segura (ex.: legado
    // svg/pdf/html do parceiro): rótulo com filename — NUNCA um data-URI de
    // conteúdo ativo do parceiro. Mesmo comportamento do portal legado (label-only).
    return (
      <div
        className={cn(
          "flex items-center gap-2 rounded border px-2 py-1 text-xs",
          onOwn ? "border-white/25 bg-white/10 text-white/90" : "bg-muted/50 text-muted-foreground",
          className
        )}
        data-testid="attachment-label"
      >
        {content.type === "image" ? "🖼️" : "📎"} {filename}
        {size > 0 ? ` · ${bytes(size)}` : ""}
      </div>
    );
  }

  // A legenda é texto puro com o link clicável (branco na bolha própria, azul-céu na dos outros). Anexo nunca tem o
  // cartão do site (spec §3.3), então aqui não há consulta; `TextoComLinks` não usa react-query nem router.
  const caption = content.caption ? (
    <div className="text-xs whitespace-pre-wrap break-words">
      <TextoComLinks texto={content.caption} tom={onOwn ? "propria" : "neutro"} />
    </div>
  ) : null;

  // VÍDEO gravado no app (06/08/2026). O <video> só nasce no clique — não há
  // `preload` que garanta isso (ver o cabeçalho de `VideoAttachment` para a
  // medição). É o mesmo cuidado que faltava nas imagens quando elas vinham em
  // base64 dentro da mensagem.
  const isVideo = content.type === "video" || (content.mime ?? "").startsWith("video/");
  const isImage = content.type === "image" || classifyPreview(content.mime, filename) === "image";

  if (isVideo) {
    // Miniatura + botão: o <video> só é montado no clique. Medido que o
    // `preload="metadata"` NÃO impede o download (readyState 4 ao abrir a
    // conversa) — a única garantia é o elemento não existir. Ver
    // `VideoAttachment` para a medição completa.
    return (
      <VideoAttachment
        src={src}
        posterSrc={content.poster_url}
        filename={filename}
        durationSec={typeof content.duration_sec === "number" ? content.duration_sec : null}
        className={className}
      />
    );
  }

  if (isImage) {
    return (
      <div className={cn("space-y-1", className)} data-testid="attachment-image">
        <ImagePreview url={src} filename={filename} baixavel={downloadable} />
        {downloadable && (
          <a
            href={src}
            target="_blank"
            rel="noopener noreferrer"
            download={filename}
            className={cn(
              "inline-flex items-center gap-1 text-[11px] underline",
              onOwn
                ? "text-white/80 hover:text-white"
                : "text-muted-foreground hover:text-foreground"
            )}
            data-testid="attachment-download"
          >
            <Paperclip className="h-3 w-3" /> Baixar
          </a>
        )}
        {caption}
      </div>
    );
  }

  // Arquivo. storage_key derivado da url de storage; b64 legado não tem → sem preview.
  const previewKind = classifyPreview(content.mime, filename);
  const storageKey = storageKeyFromSignedUrl(content.url);
  const canPreview =
    allowPreview && storageKey !== null && previewKind !== null && previewKind !== "image";

  const fileTile = (
    <>
      <Paperclip className="h-4 w-4 flex-shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="truncate font-medium">{filename}</div>
        <div className={subText}>{bytes(size)}</div>
      </div>
    </>
  );

  return (
    <div className={cn("space-y-1", className)} data-testid="attachment-file">
      {downloadable ? (
        <a
          href={src}
          target="_blank"
          rel="noopener noreferrer"
          download={filename}
          className={cn(
            "flex items-center gap-2 rounded border p-2 text-xs transition-colors",
            tileBase,
            tileHover
          )}
          data-testid="attachment-download"
        >
          {fileTile}
        </a>
      ) : (
        <div className={cn("flex items-center gap-2 rounded border p-2 text-xs", tileBase)}>
          {fileTile}
        </div>
      )}
      {canPreview && (
        <>
          <Button
            variant="outline"
            size="sm"
            className="h-7 w-full text-xs"
            onClick={() => setPreviewOpen(true)}
            data-testid="attachment-preview-btn"
          >
            <Eye className="mr-1.5 h-3 w-3" /> Visualizar
          </Button>
          {previewOpen && (
            <FilePreviewDialog
              open={previewOpen}
              onOpenChange={setPreviewOpen}
              storageKey={storageKey}
              filename={filename}
              downloadUrl={content.url}
            />
          )}
        </>
      )}
      {caption}
    </div>
  );
}

export default AttachmentMessageBubble;
